import { PushNotifications } from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
import apiClient from '@/services/axios';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';

let pushInitialized = false;

const DEVICE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function useCapacitorPushNotifications() {
  if (pushInitialized) {
    console.log('Push already initialized');
    return;
  }

  const router = useRouter();
  const toast = useToast();

  try {
    
    const permStatus = await PushNotifications.requestPermissions();
    
    if (permStatus.receive !== 'granted') {
      console.warn('Push permission not granted');
      return;
    }


    pushInitialized = true;   
    
    PushNotifications.addListener('registration', async (token) => {
      //alert('TOKEN RECEIVED: ' + token.value);

      try {
        const { identifier } = await Device.getId();
        let mobileToken: string = token.value;
        if (Capacitor.getPlatform() === 'ios') {
          const fcmTokenResult = await FirebaseMessaging.getToken();
          mobileToken = fcmTokenResult.token;
        }
        //alert(identifier)
        await apiClient.post('/mobile/push-token', {
          mobileDeviceId: identifier,
          token: mobileToken,
        });        
        //alert("mobile token" + mobileToken);  
      } catch (error) {
        const err = error as Error;
        console.error('Failed to send token:', err.message);
      }
    });

    
    PushNotifications.addListener('registrationError', (err) => {
      console.error('Registration Error:', err);
    });

    // Notification received (when app is in foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
            
      const data = notification.data;
      const deviceType = typeof data?.deviceType === 'string' && data.deviceType.length
        ? data.deviceType[0].toUpperCase() + data.deviceType.slice(1)
        : '';
      const deviceName = data?.deviceName ?? '';
      const oldStatus = data?.oldStatus ?? '';
      const newStatus = data?.newStatus ?? '';

      const message = buildToastMessage(oldStatus, newStatus, deviceType, deviceName);
      if (message) {
        toast.info(message);
      }

      // Refresh devices list
      window.dispatchEvent(new CustomEvent('devices:refresh', { detail: { page: 1 } }));
    });

  
    // UV16: a tap opens the device the notification is about. The device route
    // checks visibility itself, so a device no longer visible ends on the notice.
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const deviceId = action.notification?.data?.deviceId;
      if (typeof deviceId === 'string' && DEVICE_ID.test(deviceId)) {
        router.push({ name: 'device', params: { deviceId } });
        return;
      }
      router.push({ name: 'home' });
    });

    await PushNotifications.register();
    

  } catch (error) {
    console.error('Initialization error:', error);    
    pushInitialized = false;
  }
}

function buildToastMessage(
  oldStatus: string,
  newStatus: string,
  deviceType: string,
  deviceName: string
): string | null {
  if (!newStatus) return null;

  const key = `${oldStatus}->${newStatus}`;
  const transitions: Record<string, (_d: string, _n: string) => string> = {
    'pending->connected':              (d, n) => `Your ${d} ${n} is now successfully connected.`,
    'connected->disconnected':         (d, n) => `Your ${d} ${n} has been disconnected.`,
    'disconnected->connected':         (d, n) => `Your ${d} ${n} is connected again.`,
    'connected->connection_lost':      (d, n) => `Your ${d} ${n} lost connection.`,
    'connection_lost->connected':      (d, n) => `Your ${d} ${n} has reconnected successfully.`,
    'update_required->connected':      (d, n) => `Your ${d} ${n} has been updated and is connected again.`,
    'update_required->disconnected':   (d, n) => `Your ${d} ${n} has been disconnected and still needs an update.`,
    'connection_lost->disconnected':   (d, n) => `Your ${d} ${n} is no longer reachable and has been disconnected.`,
  };

  const transitionMessage = transitions[key];
  if (transitionMessage) return transitionMessage(deviceType, deviceName);

  const newStatusMessages: Record<string, (_d: string, _n: string) => string> = {
    pending:         (d, n) => `Your ${d} ${n} is pending connection.`,
    update_required: (d, n) => `Your ${d} ${n} requires an update.`,
  };

  const newStatusMessage = newStatusMessages[newStatus];
  if (newStatusMessage) return newStatusMessage(deviceType, deviceName);

  return `Status update for ${deviceType} ${deviceName}`;
}